import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import portfolio from "@/data/portfolio";
import caseStudies from "@/data/caseStudies";

export default function CareerHighlights() {
  return (
    <section
      id="career"
      data-testid="career-highlights-section"
      className="py-24 md:py-40 border-t hairline bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-8 h-px bg-black/40" />
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">
                02 · Career highlights
              </span>
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-black leading-[1.05] max-w-3xl">
              Selected work across events, leagues, ventures and athletics.
            </h2>
          </div>
          <p className="text-sm text-gray-500 max-w-xs">
            Flagship projects open a dedicated executive page.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {portfolio.projects.map((p, i) => (
            <ProjectCard key={p.id} project={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProjectCard({ project, index }) {
  const hasCaseStudy = Boolean(caseStudies[project.id]);

  const card = (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: index * 0.06 }}
      data-testid={`project-card-${project.id}`}
      className="group h-full text-left bg-[#F5F5F7] border hairline rounded-3xl overflow-hidden hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/5 transition-all duration-500"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-white">
        <img
          src={project.image}
          alt={project.name}
          className={`w-full h-full transition-transform duration-700 group-hover:scale-105 ${
            project.imageFit === "contain" ? "object-contain p-6 md:p-8" : "object-cover"
          }`}
          loading="lazy"
          decoding="async"
        />
        <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center">
          <ArrowUpRight className="w-3.5 h-3.5" />
        </div>
      </div>
      <div className="p-5 md:p-6">
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-500">
          {project.years}
        </div>
        <h3 className="mt-2 font-serif-display text-xl md:text-2xl text-black tracking-tight leading-tight">
          {project.name}
        </h3>
        <p className="mt-1.5 text-xs md:text-sm text-gray-600">{project.role}</p>
        {hasCaseStudy && (
          <div className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
            {project.id === "professional-show-jumper" ? "View profile →" : "View more →"}
          </div>
        )}
      </div>
    </motion.div>
  );

  if (hasCaseStudy) {
    return (
      <Link
        to={`/projects/${project.id}`}
        data-testid={`project-link-${project.id}`}
        className="block h-full focus:outline-none"
      >
        {card}
      </Link>
    );
  }

  // Fallback: open Dialog for projects without a dedicated page (e.g. Show Jumper).
  return <ProjectDialog project={project}>{card}</ProjectDialog>;
}

function ProjectDialog({ project, children }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="block h-full text-left w-full focus:outline-none"
          aria-label={`Open ${project.name} details`}
        >
          {children}
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid={`project-modal-${project.id}`}
        className="max-w-3xl bg-white rounded-3xl border-none p-0 overflow-hidden"
      >
        <div className="relative">
          <img src={project.image} alt={project.name} className="w-full h-56 md:h-72 object-cover" />
        </div>
        <div className="p-8 md:p-10">
          <DialogHeader>
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">
              {project.years}
            </div>
            <DialogTitle className="font-serif-display text-3xl md:text-4xl text-black font-light tracking-tight mt-2">
              {project.name}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600 mt-1">
              {project.role}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6">
            <h4 className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">Mission</h4>
            <p className="mt-2 text-base md:text-lg text-gray-700 font-light leading-relaxed">
              {project.mission}
            </p>
          </div>

          <div className="mt-6">
            <h4 className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">
              Key achievements
            </h4>
            <ul className="mt-3 space-y-2">
              {project.achievements.map((a) => (
                <li
                  key={a}
                  className="text-base text-gray-700 font-light leading-relaxed flex items-start gap-3"
                >
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-black flex-shrink-0" />
                  {a}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {project.tags.map((t) => (
              <span
                key={t}
                className="rounded-full bg-[#F5F5F7] border hairline px-4 py-1.5 text-xs text-gray-700"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
